import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import Icon from './AppIcon';
import Sidebar from './ui/Sidebar';
import Logo from '../assets/images/logo.png';

const MobileLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const deferredPrompt = useRef(null);

  const isEmployee = (() => { try { return JSON.parse(atob(localStorage.getItem('token')?.split('.')[1] || ''))?.role === 'employee'; } catch { return false; } })();
  const navItems = isEmployee
    ? [{ label: 'رزروهای من', path: '/booking-management', icon: 'CalendarCheck' }]
    : [
        { label: 'داشبورد', path: '/salon-dashboard', icon: 'LayoutDashboard' },
        { label: 'رزروها', path: '/booking-management', icon: 'CalendarCheck' },
        { label: 'کارمندان', path: '/employee-management', icon: 'Users' },
        { label: 'سرویس‌ها', path: '/service-management', icon: 'Scissors' },
        { label: 'تقویم', path: '/availability-calendar', icon: 'Calendar' },
      ];

  useEffect(() => {
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      deferredPrompt.current = e;
      // only show prompt on mobile-ish viewports
      if (window.innerWidth < 1024) {
        setShowInstallPrompt(true);
      }
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    const handler = () => setShowInstallPrompt(false);
    window.addEventListener('appinstalled', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt.current) return;
    deferredPrompt.current.prompt();
    const { outcome } = await deferredPrompt.current.userChoice;
    if (outcome === 'accepted') {
      deferredPrompt.current = null;
      setShowInstallPrompt(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;
  const activeItem = navItems.find((i) => isActive(i.path)) || {
    label: 'پنل مدیریت',
  };

  return (
    <div dir="rtl" className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <div className="hidden lg:block">
        <Sidebar isOpen={true} />
      </div>

      {/* Mobile top app bar */}
      <header className="lg:hidden fixed top-0 inset-x-0 h-16 bg-card/95 backdrop-blur-md border-b border-border z-40 flex items-center justify-between px-4 safe-area-top">
        <button
          onClick={() => setSidebarOpen(true)}
          className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-muted transition-colors"
          aria-label="منو">
          <Icon name="Menu" size={22} />
        </button>

        <div className="flex items-center gap-2">
          <img
            src={Logo}
            alt="logo"
            className="h-9 w-9 object-contain rounded-lg"
          />
          <h1 className="text-base font-bold text-foreground truncate max-w-[40vw]">
            {activeItem.label}
          </h1>
        </div>

        <button
          onClick={() => navigate('/user-profile')}
          className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
          aria-label="پروفایل">
          <Icon name="User" size={20} />
        </button>
      </header>

      {/* Mobile sidebar drawer */}
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Install prompt */}
      {showInstallPrompt && (
        <div className="lg:hidden fixed top-20 left-4 right-4 z-50 bg-primary text-primary-foreground rounded-2xl shadow-2xl p-4 animate-in fade-in slide-in-from-top-4">
          <div className="flex items-start gap-3">
            <img
              src={Logo}
              alt="logo"
              className="h-12 w-12 rounded-xl bg-white/20"
            />
            <div className="flex-1">
              <p className="font-bold text-sm">نصب اپلیکیشن مدیریت سالن</p>
              <p className="text-xs opacity-90 mt-1">
                با یک ضربه به صفحه اصلی اضافه کنید و سریع‌تر مدیریت کنید.
              </p>
            </div>
            <button
              onClick={() => setShowInstallPrompt(false)}
              className="p-1 rounded-full hover:bg-white/20"
              aria-label="بستن">
              <Icon name="X" size={18} />
            </button>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleInstall}
              className="flex-1 bg-white text-primary text-sm font-semibold py-2 rounded-xl">
              نصب
            </button>
            <button
              onClick={() => setShowInstallPrompt(false)}
              className="flex-1 text-sm py-2 rounded-xl hover:bg-white/10">
              بعداً
            </button>
          </div>
        </div>
      )}

      {/* Main content area */}
      <main className="lg:mr-60 min-h-screen pb-24 lg:pb-6 pt-20 lg:pt-6 px-4 lg:px-8">
        <div className="max-w-7xl mx-auto">{children}</div>
      </main>

      {/* Mobile bottom navigation */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 h-[calc(4rem+env(safe-area-inset-bottom))] bg-card/95 backdrop-blur-xl border-t border-border z-50 pb-[env(safe-area-inset-bottom)]">
        <div className="grid grid-cols-5 h-16">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center gap-1 transition-colors ${
                isActive(item.path)
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}>
              <div
                className={`p-1.5 rounded-xl transition-all ${
                  isActive(item.path) ? 'bg-primary/15' : ''
                }`}>
                <Icon
                  name={item.icon}
                  size={22}
                  strokeWidth={isActive(item.path) ? 2.5 : 2}
                />
              </div>
              <span className="text-[10px] font-medium leading-none">
                {item.label}
              </span>
            </Link>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default MobileLayout;
